import React from "react";
import moment from "moment";
import Select from "react-select";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, {
  CSVExport
} from "react-bootstrap-table2-toolkit";
import { Input, Badge, Button } from "reactstrap";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import SubUsersTableDropdown from "./subUsersTableDropdown";
import "./releaseTable.scss";
import "./subUsersTable.scss";

const STATUS_LEGEND_ROW_ID = "__sub_user_status_legend__";

const SUB_USER_TOOLBAR_SELECT_STYLES = {
  menu: (styles) => ({ ...styles, zIndex: 10 }),
  control: (styles) => ({
    ...styles,
    minHeight: 36,
    height: 36,
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: "0 4px 0 0",
    flexWrap: "nowrap",
    height: 34,
    overflow: "hidden",
  }),
  dropdownIndicator: (styles) => ({ ...styles, padding: "6px", color: "#6c757d" }),
  indicatorSeparator: () => ({ display: "none" }),
  singleValue: (styles) => ({
    ...styles,
    color: "#6c757d",
    fontSize: 14,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "Active" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const DISTRIBUTION_FILTER_OPTIONS = [
  { value: "", label: "Distribution" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const BLACKLISTED_FILTER_OPTIONS = [
  { value: "", label: "Blacklisted" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const STATUS_ITEMS = [
  { key: "distribution", label: "Distribution", field: "distribution", positive: true },
  { key: "active", label: "Active", field: "is_active", positive: true },
  { key: "blacklisted", label: "Blacklisted", field: "is_blacklisted", positive: false },
];

const isStatusLegendRow = (row) => row.isStatusLegend === true;

const SubUsersTable = ({
  setToggleModal,
  setDataModal,
  setUpdateTitle,
  setUpdateBody,
  setUpdateData,
  setUpdateBtnText,
  setUpdateBtnColor,
  setLastAction,
  forceUpdate,
  data,
  searchQuery,
  defaultSorted,
  editDisabled,
  subscriptionsEnabled = false,
  onSubscriptionBadgeClick,
}) => {
  const { ExportCSVButton } = CSVExport;

  const userFormatter = (cell, row) => {
    if (isStatusLegendRow(row)) {
      return null;
    }

    const plan = row.tenant_subscription_plan;
    const status = row.tenant_subscription_status;
    const badgeText = [plan, status].filter(Boolean).join(" · ");

    return (
      <div className="sub-user-identity">
        {row.profile_image.thumb_small ? (
          <img
            src={row.profile_image.thumb_small}
            alt={cell}
            width="36"
            height="36"
            className="rounded-circle sub-user-avatar"
          />
        ) : (
          <i className="fas fa-user-circle sub-user-avatar-placeholder" aria-hidden="true" />
        )}
        <div className="sub-user-identity-text">
          <span className="fontWeight700">{cell}</span>
          {badgeText ? (
            <Badge
              pill
              className={`sub-user-plan-badge${subscriptionsEnabled && plan ? " is-clickable" : ""}`}
              onClick={
                subscriptionsEnabled && plan && onSubscriptionBadgeClick
                  ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onSubscriptionBadgeClick(row);
                  }
                  : undefined
              }
              role={subscriptionsEnabled && plan ? "button" : undefined}
              tabIndex={subscriptionsEnabled && plan ? 0 : undefined}
              onKeyDown={
                subscriptionsEnabled && plan && onSubscriptionBadgeClick
                  ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSubscriptionBadgeClick(row);
                    }
                  }
                  : undefined
              }
            >
              {badgeText}
            </Badge>
          ) : null}
        </div>
      </div>
    );
  };

  const boolCsvFormatter = (cell) => (cell ? "Yes" : "No");

  const contractExpiryDateFormatter = (cell, row) => {
    if (isStatusLegendRow(row)) {
      return null;
    }
    return moment(cell).format("YYYY-MM-DD");
  };

  const statusFormatter = (cell, row) => {
    if (isStatusLegendRow(row)) {
      return (
        <div className="quickActionsColumn quickActionsLegendRow">
          {STATUS_ITEMS.map(({ key, label }) => (
            <div key={key} className="quickActionItem">
              <span className="quickActionLabel">{label}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="quickActionsColumn">
        {STATUS_ITEMS.map(({ key, field, positive }) => {
          const value = row[field];
          const isGood = positive ? value : !value;
          return (
            <div
              key={key}
              className="quickActionItem"
              title={`${value ? "Yes" : "No"}`}
            >
              <div className="quickActionIcon">
                <i
                  className={`fas fa-${isGood ? "check" : "times"}-circle sub-user-status-icon ${
                    isGood ? "is-good" : "is-bad"
                  }`}
                  aria-hidden="true"
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const actionFormatter = (cell, row) => {
    if (isStatusLegendRow(row)) {
      return null;
    }

    return (
      <SubUsersTableDropdown
        data={row}
        setToggleModal={setToggleModal}
        setDataModal={setDataModal}
        setUpdateTitle={setUpdateTitle}
        setUpdateBody={setUpdateBody}
        setUpdateData={setUpdateData}
        setLastAction={setLastAction}
        setUpdateBtnText={setUpdateBtnText}
        setUpdateBtnColor={setUpdateBtnColor}
        editDisabled={editDisabled}
      />
    );
  };

  const columns = [
    {
      dataField: "friendly_name",
      text: "User",
      formatter: userFormatter,
      sort: true,
      headerStyle: () => ({ width: "220px", minWidth: "220px" }),
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      headerStyle: () => ({ width: "130px" }),
    },
    {
      dataField: "first_name",
      text: "First Name",
      sort: true,
      headerStyle: () => ({ width: "130px" }),
    },
    {
      dataField: "last_name",
      text: "Last Name",
      sort: true,
      headerStyle: () => ({ width: "140px" }),
    },
    {
      dataField: "company",
      text: "Company",
      sort: true,
      headerStyle: () => ({ width: "120px" }),
    },
    {
      dataField: "contract_expiry",
      text: "Contract Expiry",
      formatter: contractExpiryDateFormatter,
      csvFormatter: (cell, row) => (isStatusLegendRow(row) ? "" : moment(cell).format("YYYY-MM-DD")),
      sort: true,
      headerStyle: () => ({ width: "150px" }),
    },
    {
      dataField: "distribution",
      text: "Status",
      formatter: statusFormatter,
      sort: false,
      csvExport: false,
      classes: "sub-user-status-col",
      headerClasses: "sub-user-status-col",
      headerStyle: () => ({ width: "240px", minWidth: "240px", textAlign: "center" }),
    },
    {
      dataField: "tenant_subscription_plan",
      text: "Plan",
      hidden: true,
      sort: true,
      csvExport: true,
    },
    {
      dataField: "distribution",
      text: "Distribution",
      hidden: true,
      csvFormatter: boolCsvFormatter,
      csvExport: true,
    },
    {
      dataField: "is_active",
      text: "Active",
      hidden: true,
      csvFormatter: boolCsvFormatter,
      sort: true,
      csvExport: true,
    },
    {
      dataField: "is_blacklisted",
      text: "Blacklisted",
      hidden: true,
      csvFormatter: boolCsvFormatter,
      csvExport: true,
    },
    {
      dataField: "action",
      text: "Action",
      formatter: actionFormatter,
      sort: false,
      csvExport: false,
      headerStyle: () => ({ width: "111px" }),
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "first_name" || sortField === "last_name" || sortField === "email" || sortField === "friendly_name" || sortField === "company" || sortField === "tenant_subscription_plan" || sortField === "contract_expiry" || sortField === "is_active") {
      searchQuery.setOrdering({ order: ascDesc, name: sortField, sort: sortField });
    }
  };

  const displayTableData = data.length > 0
    ? [{ id: STATUS_LEGEND_ROW_ID, isStatusLegend: true }, ...data]
    : data;

  const rowHighlightStyle = (row) => (isStatusLegendRow(row) ? "quick-actions-legend-row" : "");

  const renderFilterSelect = (options, value, onChange, ariaLabel) => {
    const selected = options.find((opt) => opt.value === value) ?? options[0];
    return (
      <div className="release-toolbar-status-filter">
        <div className="release-toolbar-select-inner">
          <Select
            className="release-status-select"
            classNamePrefix="release-status"
            components={{ IndicatorSeparator: () => null }}
            options={options}
            value={selected}
            onChange={(selectedOption) => onChange(selectedOption?.value ?? "")}
            styles={SUB_USER_TOOLBAR_SELECT_STYLES}
            isSearchable={false}
            aria-label={ariaLabel}
          />
        </div>
      </div>
    );
  };

  return (
    <ToolkitProvider
      keyField="id"
      data={data}
      columns={columns}
      search={{
        searchFormatted: true
      }}
      exportCSV={{
        fileName: "sub_users.csv",
      }}
    >
      {(props) => (
        <div>
          <div className="release-toolbar release-toolbar--table">
            <div className="release-toolbar-filters">
              <div className="customSearch">
                <Input
                  placeholder="Search..."
                  value={searchQuery.query}
                  onChange={(e) => searchQuery.setQuery(e.target.value)}
                />
              </div>
              {renderFilterSelect(
                ACTIVE_FILTER_OPTIONS,
                searchQuery.filters.is_active,
                (value) => searchQuery.onFilterChange("is_active", value),
                "Filter by active status"
              )}
              {renderFilterSelect(
                DISTRIBUTION_FILTER_OPTIONS,
                searchQuery.filters.distribution,
                (value) => searchQuery.onFilterChange("distribution", value),
                "Filter by distribution"
              )}
              {renderFilterSelect(
                BLACKLISTED_FILTER_OPTIONS,
                searchQuery.filters.is_blacklisted,
                (value) => searchQuery.onFilterChange("is_blacklisted", value),
                "Filter by blacklisted status"
              )}
              {searchQuery.hasActiveFilters ? (
                <Button
                  className="btn btn-outline-info release-toolbar-filters-reset"
                  onClick={searchQuery.resetFilters}
                >
                  Reset
                </Button>
              ) : null}
            </div>
            <div className="release-toolbar-actions">
              <ExportCSVButton
                className="btn btn-outline-info"
                {...props.csvProps}
              >
                Export CSV
              </ExportCSVButton>
            </div>
          </div>

          <BootstrapTable
            {...props.baseProps}
            data={displayTableData}
            bordered={false}
            remote={{ sort: true }}
            defaultSorted={defaultSorted}
            onTableChange={handleTableChange}
            rowClasses={rowHighlightStyle}
            classes="sub-users-table"
          />
        </div>
      )}
    </ToolkitProvider>
  );
};

export default SubUsersTable;
